import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ResponseApi, ResponseListApi } from '@core/models/interfaces/common';
import { GetLeadNoteParams, LeadNote, LeadNoteCreate, LeadReplyNote } from '@core/models/interfaces/lead';
import {
  GetNoteLeadParams,
  NoteGroup,
  NoteLead,
  NotePolicy,
  NoteStatus,
  NoteStatusCreate
} from '@core/models/interfaces/note';
import { GetUserParams, UserDetail } from '@core/models/interfaces/user';
import { buildHttpParams } from '@shared/utils/buildHttpParams';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class NoteService {
  private baseUrl = `${environment.API_URL}/crm`;
  private noteUrl = `${this.baseUrl}/notes`;
  private noteStatusUrl = `${this.baseUrl}/note-status`;

  constructor(private httpClient: HttpClient) {}

  // LEAD NOTE
  getLeadNoteById(id: string) {
    return this.httpClient.get<ResponseApi<LeadNote>>(`${this.noteUrl}/${id}`);
  }

  updateLeadNoteById(id: string, body: LeadNoteCreate) {
    return this.httpClient.put<ResponseApi<LeadNote>>(`${this.noteUrl}/${id}`, body);
  }

  deleteLeadNoteById(id: string) {
    return this.httpClient.delete(`${this.noteUrl}/${id}`);
  }

  getLeadNotes(params: GetLeadNoteParams) {
    const queryParams = params ? buildHttpParams(params) : undefined;

    return this.httpClient.get<ResponseListApi<LeadNote>>(`${this.noteUrl}`, {
      params: queryParams
    });
  }

  getLeadNotesGroupBy(params: GetLeadNoteParams) {
    const queryParams = params ? buildHttpParams(params) : undefined;
    return this.httpClient.get<ResponseListApi<NoteGroup>>(`${this.noteUrl}/group/lead`, {
      params: queryParams
    });
  }

  getNotePageByLeadId(params: GetLeadNoteParams) {
    const queryParams = params ? buildHttpParams(params) : undefined;
    return this.httpClient.get<ResponseListApi<LeadNote>>(`${this.noteUrl}/lead/${params.leadId}`, {
      params: queryParams
    });
  }

  createLeadNote(body: LeadNoteCreate) {
    return this.httpClient.post<ResponseApi<LeadNote>>(`${this.noteUrl}`, body);
  }

  createLeadNoteDraft(body: LeadNoteCreate) {
    return this.httpClient.post<ResponseApi<LeadNote>>(`${this.noteUrl}/draft`, body);
  }

  replyLeadNote(parentId: string, message: string) {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.httpClient.post<ResponseApi<LeadReplyNote>>(`${this.noteUrl}/reply/${parentId}`, message, {
      headers: headers
    });
  }

  replyLeadNoteDraft(parentId: string, message: string) {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.httpClient.post<ResponseApi<LeadReplyNote>>(
      `${this.noteUrl}/reply/${parentId}/draft`,
      {
        notes: message,
        isDraft: true
      },
      {
        headers: headers
      }
    );
  }

  // NOTE STATUS
  getNoteStatusById(id: string) {
    return this.httpClient.get<ResponseApi<NoteStatus>>(`${this.noteStatusUrl}/${id}`);
  }

  updateNoteStatusById(id: string, body: NoteStatusCreate) {
    return this.httpClient.put<ResponseApi<NoteStatus>>(`${this.noteStatusUrl}/${id}`, body);
  }

  deleteNoteStatusById(id: string) {
    return this.httpClient.delete(`${this.noteStatusUrl}/${id}`);
  }

  getNoteStatuses(params?: { page?: number; size?: number; sort?: number; keyword?: string }) {
    const httpParams = params ? buildHttpParams(params) : undefined;
    return this.httpClient.get<ResponseListApi<NoteStatus>>(`${this.noteStatusUrl}`, {
      params: httpParams
    });
  }

  createNoteStatus(body: NoteStatusCreate) {
    return this.httpClient.post(`${this.noteStatusUrl}`, body);
  }

  getUsersNoteReport(params?: GetUserParams) {
    const noteUrl = `${this.baseUrl}/report/note`;
    return this.httpClient.get<ResponseListApi<UserDetail>>(`${noteUrl}/users`, { params: { ...params } });
  }

  getLeadForNotesManagement(params?: GetNoteLeadParams) {
    return this.httpClient.get<ResponseListApi<NoteLead>>(`${this.noteUrl}/leads`, { params: { ...params } });
  }

  getPolicyForNotesManagement(params?: GetNoteLeadParams) {
    return this.httpClient.get<ResponseListApi<NotePolicy>>(`${this.noteUrl}/policies`, { params: { ...params } });
  }
}
